/**
 * RecordingButton — The entry-point button for the Recording feature.
 *
 * All phases render inside the same top-right pill container:
 *   idle        → "Record" text
 *   expanded    → Settings | Teleprompter | Record | X
 *   frameSelect → Settings | Teleprompter | X Cancel | ● Start Recording
 *   recording   → Settings | Teleprompter | Pause | Stop | Timer
 *   finished    → Download | Discard
 */

import React from "react";

import { useAppI18n } from "../hooks/useAppI18n";

import type { RecordingPhase } from "../hooks/useRecordingState";

/** Format seconds as MM:SS */
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

interface RecordingButtonProps {
  phase: RecordingPhase;
  elapsedSeconds: number;
  isPaused: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onRecord: () => void;
  onOpenSettings: () => void;
  onToggleTeleprompter: () => void;
  isTeleprompterOpen: boolean;
  onCancel: () => void;
  onStartRecording: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onDownload: () => void;
  onDiscard: () => void;
  isConverting?: boolean;
}

export const RecordingButton: React.FC<RecordingButtonProps> = ({
  phase,
  elapsedSeconds,
  isPaused,
  onExpand,
  onCollapse,
  onRecord,
  onOpenSettings,
  onToggleTeleprompter,
  isTeleprompterOpen,
  onCancel,
  onStartRecording,
  onPause,
  onResume,
  onStop,
  onDownload,
  onDiscard,
  isConverting = false,
}) => {
  const { t } = useAppI18n();

  // ── Collapsed state — text-only, part of the unified pill ───────────────
  if (phase === "idle") {
    return (
      <button
        type="button"
        className="recording-btn recording-btn--collapsed"
        onClick={onExpand}
        title={t("recording.record")}
      >
        <span className="recording-btn__label">{t("recording.record")}</span>
      </button>
    );
  }

  // ── Settings + Teleprompter + One-click video (shared across expanded/frameSelect/recording) ──
  const settingsAndTeleprompterIcons = (
    <>
      <button
        type="button"
        className="recording-btn-group__icon-btn"
        onClick={onOpenSettings}
        title={t("recording.recordSettings")}
      >
        <span className="material-symbols-outlined">settings</span>
      </button>

      <button
        type="button"
        className={`recording-btn-group__icon-btn ${
          isTeleprompterOpen ? "recording-btn-group__icon-btn--active" : ""
        }`}
        onClick={onToggleTeleprompter}
        title={t("recording.teleprompter")}
      >
        <span className="material-symbols-outlined">description</span>
      </button>
    </>
  );

  // ── Expanded state — Settings | Teleprompter | Record | X ──────────────
  if (phase === "expanded") {
    return (
      <div className="recording-btn-group">
        {settingsAndTeleprompterIcons}

        <button
          type="button"
          className="recording-btn-group__record-btn"
          onClick={onRecord}
          title={t("recording.startRecording")}
        >
          <span className="recording-btn__dot" />
          <span className="recording-btn__label">{t("recording.record")}</span>
        </button>

        <button
          type="button"
          className="recording-btn-group__close-btn"
          onClick={onCollapse}
          title={t("recording.close")}
          aria-label={t("recording.close")}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>
    );
  }

  // ── Frame-select state — Settings | Teleprompter | X Cancel | Start Recording ──
  if (phase === "frameSelect") {
    return (
      <div className="recording-btn-group">
        {settingsAndTeleprompterIcons}

        <button
          type="button"
          className="recording-btn-group__cancel-btn"
          onClick={onCancel}
          title={t("recording.cancel")}
        >
          <span className="material-symbols-outlined">close</span>
          <span>{t("recording.cancel")}</span>
        </button>

        <button
          type="button"
          className="recording-btn-group__start-btn"
          onClick={onStartRecording}
          title={t("recording.startRecording")}
        >
          <span className="recording-btn-group__start-dot" />
          <span>{t("recording.startRecording")}</span>
        </button>
      </div>
    );
  }

  // ── Recording state — Settings | Teleprompter | Pause | Stop | Timer ───
  if (phase === "recording") {
    return (
      <div className="recording-btn-group">
        {settingsAndTeleprompterIcons}

        {isPaused ? (
          <button
            type="button"
            className="recording-btn-group__pause-btn recording-btn-group__pause-btn--resume"
            onClick={onResume}
            title={t("recording.resume")}
          >
            <span className="material-symbols-outlined">play_arrow</span>
            <span>{t("recording.resume")}</span>
          </button>
        ) : (
          <button
            type="button"
            className="recording-btn-group__pause-btn"
            onClick={onPause}
            title={t("recording.pause")}
          >
            <span className="material-symbols-outlined">pause</span>
            <span>{t("recording.pause")}</span>
          </button>
        )}

        <button
          type="button"
          className="recording-btn-group__stop-btn"
          onClick={onStop}
          title={t("recording.stop")}
        >
          <span className="material-symbols-outlined">stop</span>
          <span>{t("recording.stop")}</span>
        </button>

        <div className="recording-btn-group__timer">
          <span className="recording-btn-group__timer-dot" />
          <span className="recording-btn-group__timer-text">
            {formatTime(elapsedSeconds)}
          </span>
        </div>
      </div>
    );
  }

  // ── Finished state — Discard (left) | spacer | Download (right) ─────────
  if (phase === "finished") {
    return (
      <div className="recording-btn-group recording-btn-group--finished">
        <button
          type="button"
          className="recording-btn-group__discard-btn"
          onClick={onDiscard}
          title={t("recording.discard")}
        >
          <span className="material-symbols-outlined">delete</span>
          <span>{t("recording.discard")}</span>
        </button>

        <div className="recording-btn-group__spacer" />

        <button
          type="button"
          className="recording-btn-group__download-btn"
          onClick={onDownload}
          title={
            isConverting
              ? t("recording.convertingToMp4")
              : t("recording.download")
          }
          disabled={isConverting}
        >
          <span className="material-symbols-outlined">
            {isConverting ? "hourglass_top" : "download"}
          </span>
          <span>
            {isConverting ? t("recording.converting") : t("recording.download")}
          </span>
        </button>
      </div>
    );
  }

  return null;
};
